import { BIOMES, TERRAIN, CLIMATE } from '../config';
import { biomeAt } from '../engine/biome';
import { terrainHeight } from '../engine/terrain';

// Bake a top-down biome image of the island a given SEED would generate, for the main-menu server previews.
// Terrain + climate are PURE functions of their seeds, so we swap the target seed in, sample the field into a
// canvas, then restore whatever was set. Progressive: a few rows per animation frame with a 0..1 progress
// callback, so a fresh-seed preview shows a real LOADING BAR and never hitches the menu. Same colouring as the
// in-game map (ui/map.ts) so the preview matches the actual island you drop into.

function rgb(n: number): [number, number, number] { return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; }

export function bakeMapPreview(seed: number, size: number, onProgress?: (frac: number) => void): Promise<HTMLCanvasElement> {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    const img = ctx.createImageData(size, size);
    const d = img.data;
    const R = TERRAIN.islandInner * 1.34;                 // world half-extent baked: the land disc + its coast
    const savedT = TERRAIN.seed, savedC = CLIMATE.seed;   // restore after, so nothing else sees the swapped seed
    TERRAIN.seed = seed; CLIMATE.seed = seed + 2905;      // same derivation main.ts uses (1337 -> climate 4242)
    let py = 0;
    const rowsPerFrame = Math.max(2, Math.floor(size / 24));
    const step = (): void => {
      const end = Math.min(size, py + rowsPerFrame);
      for (; py < end; py++) {
        const wz = (py / (size - 1)) * 2 * R - R;
        for (let px = 0; px < size; px++) {
          const wx = (px / (size - 1)) * 2 * R - R;
          const h = terrainHeight(wx, wz);
          let r: number, g: number, b: number;
          if (h < TERRAIN.seaLevel) {
            const depth = Math.min(1, (TERRAIN.seaLevel - h) / 90);
            r = 60 - depth * 34; g = 116 - depth * 50; b = 168 - depth * 40; // sea, darkening with depth
          } else {
            const [cr, cg, cb] = rgb(BIOMES[biomeAt(wx, wz, h)].grass);
            const shade = 0.66 + 0.5 * Math.min(1, (h - TERRAIN.seaLevel) / 320); // lighten with altitude
            r = Math.min(255, cr * shade); g = Math.min(255, cg * shade); b = Math.min(255, cb * shade);
          }
          const i = (py * size + px) * 4;
          d[i] = r; d[i + 1] = g; d[i + 2] = b; d[i + 3] = 255;
        }
      }
      ctx.putImageData(img, 0, 0);
      onProgress?.(py / size);
      if (py < size) { requestAnimationFrame(step); return; }
      TERRAIN.seed = savedT; CLIMATE.seed = savedC;       // hand the globals back untouched
      resolve(canvas);
    };
    requestAnimationFrame(step);
  });
}
